import BlockUi from '@availity/block-ui';
import React from 'react';
import { connect } from 'react-redux';
import { Button, Dropdown, Form, Header, Segment, StrictDropdownProps } from 'semantic-ui-react';
import { getRidershipDataReadiness } from '../../../actions/ridershipActions';
import { AppState } from '../../../reducers';
import { getSelectedOrDefaultAgency } from '../../../selectors';
import type { RidershipDataReadinessResponse } from '../../../types/ridershipReadinessTypes';
import DailyPipelinePanel from './DailyPipelinePanel';
import FiscalMonthGrid from './FiscalMonthGrid';
import { getFiscalYearDailyRange } from './fiscalYearDailyRange';
import ReadinessIssuesList from './ReadinessIssuesList';

const styles = {
    intro: {
        marginBottom: '1.5rem',
        color: 'rgba(0,0,0,.75)',
    },
};

function getCurrentFiscalYear(): number {
    const now = new Date();
    return now.getMonth() >= 9 ? now.getFullYear() + 1 : now.getFullYear();
}

function buildFiscalYearOptions(currentYear: number) {
    const options = [];
    for (let year = 2022; year <= currentYear; year++) {
        options.push({ key: year, text: `FY ${year}`, value: year });
    }
    return options;
}

interface Props {
    agency: ReturnType<typeof getSelectedOrDefaultAgency>;
}

const RidershipDataReadinessPage: React.FC<Props> = ({ agency }) => {
    const [blocking, setBlocking] = React.useState(false);
    const [data, setData] = React.useState<RidershipDataReadinessResponse | null>(null);
    const [fiscalYear, setFiscalYear] = React.useState(getCurrentFiscalYear());
    const [refreshNonce, setRefreshNonce] = React.useState(0);

    const loadData = React.useCallback(async () => {
        if (!agency?.id) {
            return;
        }
        setBlocking(true);
        try {
            const timeZoneName = agency.timeZoneName || 'UTC';
            const { dailyFrom, dailyTo } = getFiscalYearDailyRange(fiscalYear, timeZoneName);
            const response = await getRidershipDataReadiness(agency.id, fiscalYear, dailyFrom, dailyTo);
            setData(response);
        } catch {
            setData(null);
        } finally {
            setBlocking(false);
        }
    }, [agency?.id, fiscalYear, refreshNonce]);

    React.useEffect(() => {
        void loadData();
    }, [loadData]);

    const onFiscalYearChange: StrictDropdownProps['onChange'] = (_, { value }) => {
        const parsed = typeof value === 'number' ? value : Number(value);
        if (!Number.isNaN(parsed) && parsed !== fiscalYear) {
            setData(null);
            setFiscalYear(parsed);
        }
    };

    const onRefresh = () => {
        setRefreshNonce(value => value + 1);
    };

    return (
        <BlockUi blocking={blocking}>
            <Form>
                <Header as="h1" className="reportHeader">
                    Ridership Data Sync Status
                </Header>
                <p style={styles.intro}>
                    METRO uploads daily APC (Ridecheck) files and monthly fiscal-year CSVs to storage.
                    Portal ridership reports only reflect data after it is processed in Databricks.
                    Use this page to see whether daily and fiscal-year inputs are present and processed.
                </p>
                <Form.Group>
                    <Form.Field width={4}>
                        <label>Fiscal year</label>
                        <Dropdown
                            selection
                            options={buildFiscalYearOptions(getCurrentFiscalYear())}
                            value={fiscalYear}
                            onChange={onFiscalYearChange}
                        />
                    </Form.Field>
                    <Form.Field width={4}>
                        <label>&nbsp;</label>
                        <Button primary onClick={onRefresh}>Refresh</Button>
                    </Form.Field>
                </Form.Group>

                {data && (
                    <>
                        <Segment>
                            <DailyPipelinePanel daily={data.dailyPipeline} />
                        </Segment>

                        <Segment>
                            <FiscalMonthGrid
                                fiscal={data.fiscalPipeline}
                                fiscalYear={fiscalYear}
                                timeZoneName={agency?.timeZoneName || 'UTC'}
                            />
                        </Segment>

                        <Segment>
                            <ReadinessIssuesList
                                issues={data.issues}
                                fiscalYear={fiscalYear}
                                timeZoneName={agency?.timeZoneName || 'UTC'}
                            />
                        </Segment>
                    </>
                )}
            </Form>
        </BlockUi>
    );
};

export default connect(
    (state: AppState) => ({
        agency: getSelectedOrDefaultAgency(state),
    }),
)(RidershipDataReadinessPage);
